import {
  useState,
  useEffect,
  useMemo,
  useCallback,
  type ReactElement,
} from 'react';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Building2,
  RefreshCw,
  AlertCircle,
  Loader2,
  X,
  Filter,
} from 'lucide-react';
import { chainsService } from '../../services/api';
import { ChainModal } from './modals/ChainModal';
import type { Chain, Zone, ChainStats } from '../../types/api';
import { useToast } from '../../hooks/useToast';
import ToastContainer from '../ui/ToastContainer';

const ChainsManagement = (): ReactElement => {
  const [chains, setChains] = useState<Chain[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [stats, setStats] = useState<ChainStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedZone, setSelectedZone] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedChain, setSelectedChain] = useState<Chain | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { addToast, toasts, removeToast } = useToast();

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') ?? 'null') as {
        role?: string;
      } | null;
    } catch {
      return null;
    }
  }, []);

  const canManageChains = ['SYSTEM_ADMIN', 'ADMIN'].includes(user?.role ?? '');

  // Cargar datos
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [chainsData, statsData] = await Promise.all([
        chainsService.getAll(),
        chainsService.getStats(),
      ]);

      setChains(chainsData);
      setStats(statsData);

      // Extraer zonas únicas
      const uniqueZones = Array.from(
        new Map(chainsData.map((c) => [c.zone.id, c.zone])).values()
      );
      setZones(uniqueZones);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canManageChains) {
      void loadData();
    }
  }, [canManageChains, loadData]);

  // Filtros activos
  const hasActiveFilters = useMemo(() => {
    return !!(searchTerm || selectedZone);
  }, [searchTerm, selectedZone]);

  // Cadenas filtradas
  const filteredChains = useMemo(() => {
    let result = chains;

    if (searchTerm) {
      result = result.filter((chain) =>
        chain.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedZone) {
      result = result.filter((chain) => chain.zone.id === selectedZone);
    }

    return result;
  }, [chains, searchTerm, selectedZone]);

  // Limpiar filtros
  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedZone('');
  }, []);

  // CRUD Handlers
  const handleCreate = useCallback(
    async (data: { name: string; zoneId: string }) => {
      setIsSaving(true);
      try {
        await chainsService.create(data);
        addToast('success', 'Cadena creada correctamente');
        await loadData();
        setIsCreating(false);
      } catch (error: any) {
        addToast(
          'error',
          error.response?.data?.message || 'Error al crear cadena'
        );
      } finally {
        setIsSaving(false);
      }
    },
    [addToast, loadData]
  );

  const handleUpdate = useCallback(
    async (data: { name: string; zoneId: string }) => {
      if (!selectedChain) return;

      setIsSaving(true);
      try {
        await chainsService.update(selectedChain.id, data);
        addToast('success', 'Cadena actualizada correctamente');
        await loadData();
        setIsEditing(false);
        setSelectedChain(null);
      } catch (error: any) {
        addToast(
          'error',
          error.response?.data?.message || 'Error al actualizar cadena'
        );
      } finally {
        setIsSaving(false);
      }
    },
    [selectedChain, addToast, loadData]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      if (
        !confirm('¿Eliminar esta cadena? Esta acción no se puede deshacer.')
      ) {
        return;
      }

      try {
        await chainsService.delete(id);
        addToast('success', 'Cadena eliminada correctamente');
        await loadData();
      } catch (error: any) {
        addToast(
          'error',
          error.response?.data?.message || 'Error al eliminar cadena'
        );
      }
    },
    [addToast, loadData]
  );

  // Helpers
  const getZoneColor = (zoneName: string) => {
    const colors: Record<string, string> = {
      NORTE: 'bg-blue-100 text-blue-800 border-blue-200',
      CENTRO: 'bg-green-100 text-green-800 border-green-200',
      SUR: 'bg-orange-100 text-orange-800 border-orange-200',
    };
    return colors[zoneName] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Sin permisos
  if (!canManageChains) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
            <span className="text-red-800">
              No tienes permisos para gestionar cadenas
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Gestión de Cadenas
          </h1>
          <p className="text-gray-600">Administra las cadenas comerciales</p>
        </div>
        <button
          onClick={() => {
            setIsCreating(true);
          }}
          className="bg-primary-medium hover:bg-primary-dark text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Nueva Cadena
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-gray-400">
            <p className="text-sm text-gray-600">Total</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-400">
            <p className="text-sm text-gray-600">Zona Norte</p>
            <p className="text-2xl font-bold text-blue-900">
              {stats.byZone.NORTE}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-400">
            <p className="text-sm text-gray-600">Zona Centro</p>
            <p className="text-2xl font-bold text-green-900">
              {stats.byZone.CENTRO}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-orange-400">
            <p className="text-sm text-gray-600">Zona Sur</p>
            <p className="text-2xl font-bold text-orange-900">
              {stats.byZone.SUR}
            </p>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Buscar cadenas..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent w-full"
            />
          </div>
          <div className="relative w-full md:w-48">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <select
              value={selectedZone}
              onChange={(e) => {
                setSelectedZone(e.target.value);
              }}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent w-full appearance-none"
            >
              <option value="">Todas las zonas</option>
              {zones.map((zone) => (
                <option key={zone.id} value={zone.id}>
                  {zone.name}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={() => void loadData()}
            disabled={loading}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Active Filters */}
        {hasActiveFilters && (
          <div className="flex items-center gap-2 mt-4 pt-4 border-t">
            <span className="text-sm text-gray-600">Filtros activos:</span>
            {searchTerm && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                Búsqueda: "{searchTerm}"
                <button
                  onClick={() => {
                    setSearchTerm('');
                  }}
                  className="hover:bg-blue-200 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {selectedZone && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                Zona: {zones.find((z) => z.id === selectedZone)?.name}
                <button
                  onClick={() => {
                    setSelectedZone('');
                  }}
                  className="hover:bg-green-200 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            <button
              onClick={clearFilters}
              className="text-xs text-gray-600 hover:text-gray-900 underline ml-2"
            >
              Limpiar filtros
            </button>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary-medium" />
          <span className="ml-2 text-gray-600">Cargando cadenas...</span>
        </div>
      ) : (
        /* Tabla */
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cadena
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Zona
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredChains.length > 0 ? (
                  filteredChains.map((chain) => (
                    <tr key={chain.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Building2 className="h-5 w-5 text-gray-400 mr-3" />
                          <div className="text-sm font-medium text-gray-900">
                            {chain.name}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-3 py-1 text-xs font-medium rounded-full border ${getZoneColor(
                            chain.zone.name
                          )}`}
                        >
                          {chain.zone.name}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => {
                            setSelectedChain(chain);
                            setIsEditing(true);
                          }}
                          className="text-primary-medium hover:text-primary-dark mr-3"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => void handleDelete(chain.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={3}
                      className="px-6 py-4 text-center text-gray-500"
                    >
                      {hasActiveFilters
                        ? 'No se encontraron cadenas con los filtros aplicados'
                        : 'No hay cadenas registradas'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modales */}
      <ChainModal
        isOpen={isCreating}
        onClose={() => {
          setIsCreating(false);
        }}
        onSave={handleCreate}
        zones={zones}
        isLoading={isSaving}
      />

      <ChainModal
        isOpen={isEditing}
        onClose={() => {
          setIsEditing(false);
          setSelectedChain(null);
        }}
        onSave={handleUpdate}
        chain={selectedChain}
        zones={zones}
        isLoading={isSaving}
      />
    </div>
  );
};

export default ChainsManagement;
